/**
 * Per-Speaker Phase — the phase counterpart of Per-Speaker FR (see
 * `./per-speaker-freq-response.ts` for the shared reasoning: same speaker
 * reshape, `computePerSpeakerPhaseGeometry` instead of the FR variant).
 * Same dash-cycle + legend convention, not colour.
 */
import { canvasTypeFont } from '@roomtreatment/design';
import {
  computePerSpeakerPhaseGeometry,
  type PerSpeakerInput,
  type PerSpeakerSeries,
} from '../perSpeaker';
import type { ChartPad, PlotFrame } from '../layout';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawSeriesLine, INK, DASH_CYCLE } from './_shared';

// Same pad box as Per-Speaker FR (web's `PAD_PER_SPEAKER` is reused by both
// sections too) — named identically per-file rather than inventing a
// distinct constant for an identical value.
export const PER_SPEAKER_PAD: ChartPad = { t: 14, r: 14, b: 26, l: 40 };

function speakerLabel(s: { index: number; subkind: string | null }): string {
  return `${s.index + 1} · ${s.subkind ?? 'speaker'}`;
}

function drawLegend(ctx: CanvasRenderingContext2D, frame: PlotFrame, series: PerSpeakerSeries[]): void {
  ctx.font = canvasTypeFont('label');
  let lx = frame.plotX + 6;
  const ly = frame.plotY + 8;
  series.forEach((s, i) => {
    const label = speakerLabel(s);
    ctx.setLineDash(DASH_CYCLE[i % DASH_CYCLE.length]!);
    ctx.strokeStyle = INK.primary;
    ctx.lineWidth = i === 0 ? 2.0 : 1.4;
    ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx + 14, ly); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = INK.label;
    ctx.textAlign = 'left';
    ctx.fillText(label, lx + 17, ly + 3);
    lx += 17 + ctx.measureText(label).width + 10;
  });
}

export interface DrawPerSpeakerPhaseOpts {
  freqs: number[];
  speakers: PerSpeakerInput[];
  fMin?: number;
  fMax?: number;
  width: number;
  height: number;
}

export function drawPerSpeakerPhaseCanvas(canvas: HTMLCanvasElement, opts: DrawPerSpeakerPhaseOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computePerSpeakerPhaseGeometry({
    freqs: opts.freqs,
    speakers: opts.speakers,
    width: opts.width,
    height: opts.height,
    pad: PER_SPEAKER_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    unit: 'deg',
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No per-speaker data yet');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);
  geo.series.forEach((series, i) => {
    drawSeriesLine(ctx, geo.frame, series.points, INK.primary, i === 0 ? 2.0 : 1.4, DASH_CYCLE[i % DASH_CYCLE.length]);
  });
  drawLegend(ctx, geo.frame, geo.series);
}
